import { PayloadAction } from '@reduxjs/toolkit';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { hasUnitPremiumFeature } from 'utility/premiumFeatureCheck';
import * as unitSelectors from 'views/screens/Units/reducers';
import * as actions from 'views/screens/vehicleMaintenance/actions/preventiveNotices/noticesByDate';
import * as schemas from 'views/screens/vehicleMaintenance/schemas/preventiveNotices/notices';
import {
  deleteNotice,
  getNoticeByDateList,
  saveNoticeByDate
} from 'views/screens/vehicleMaintenance/services/noticeService';
import { NoticeByDateRequest } from 'views/screens/vehicleMaintenance/types';
import * as types from 'views/screens/vehicleMaintenance/types/preventiveNotices/noticesByDate';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchNoticesList(action) {
  try {
    const jsonResult = yield call(getNoticeByDateList);
    const entitiesCop = jsonResult.map((result) => camelcaseKeys(result));

    // Filtrar unidades sin useMaintenance
    const units = yield select(unitSelectors.getUnitsList);
    const filteredByPremiumFeature = entitiesCop.filter((notice) => {
      const unit = units.find((u) => u.unitId === notice.unitId);
      return unit ? hasUnitPremiumFeature(unit, 'useMaintenance') : false;
    });

    const {
      entities: { notices },
      result //order
    } = normalize(filteredByPremiumFeature, schemas.notices); //normalize data to byId and order
    yield put(actions.completeFetchingNotices(notices, result));
  } catch (err) {
    const error = {
      errorMessage: err?.toString() ?? '',
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failFetchingNotices(error));
  }
}

export function* watchFetchNoticesList() {
  yield takeEvery(types.NOTICES_BY_DATE_FETCH_STARTED, fetchNoticesList);
}

/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchNoticesList(action) {}

export function* watchPartialFetchNoticesList() {
  yield takeEvery(types.NOTICES_BY_DATE_PARTIAL_FETCH_STARTED, partialFetchNoticesList);
}
/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addNotice(action: PayloadAction<NoticeByDateRequest>) {
  const request = action.payload;
  try {
    const jsonResult = yield call(saveNoticeByDate, request);
    const addedData = camelcaseKeys(jsonResult);
    yield put(
      actions.completeAddingNotice(`${addedData.unitId}-${addedData.noticeId}`, request.tempId, {
        ...request,
        ...addedData
      })
    );
  } catch (e) {
    yield put(actions.failAddingNotice(request.tempId, e));
  }
}

export function* watchAddNoticesStarted() {
  yield takeEvery(types.NOTICE_BY_DATE_ADD_STARTED, addNotice);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editNotice(action: PayloadAction<NoticeByDateRequest>) {
  const request = action.payload;
  try {
    const jsonResult = yield call(saveNoticeByDate, request);
    const addedData = camelcaseKeys(jsonResult);
    yield put(
      actions.completeEditingNotice({
        ...request,
        ...addedData
      })
    );
  } catch (e) {
    yield put(actions.failEditingNotice(`${request.unitId}-${request.noticeId}`, e));
  }
}

export function* watchEditNoticesStarted() {
  yield takeEvery(types.NOTICE_BY_DATE_EDIT_STARTED, editNotice);
}

/* -------------------------------------------------------------------------- */
/*                                    RENEW                                    */
/* -------------------------------------------------------------------------- */
function* renewNotice(action: PayloadAction<NoticeByDateRequest>) {
  const request = action.payload;
  try {
    /*
    const jsonResult = yield call(renewNoticeByDate, request);
    const addedData = camelcaseKeys(jsonResult);
    yield put(
      actions.completeRenewNotice({
        ...request,
        ...addedData
      })
    );
    */
  } catch (e) {
    yield put(actions.failRenewNotice(`${request.unitId}-${request.noticeId}`, e));
  }
}

export function* watchRenewNoticesStarted() {
  yield takeEvery(types.NOTICE_BY_DATE_RENEW_STARTED, renewNotice);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeNotice(action: PayloadAction<{ noticeId: number; unitId: number }>) {
  const request = action.payload;
  try {
    yield call(deleteNotice, request);
    yield put(actions.completeRemovingNotice(`${request.unitId}-${request.noticeId}`));
  } catch (e) {
    yield put(actions.failRemovingNotice(`${request.unitId}-${request.noticeId}`, e));
  }
}

export function* watchRemoveNoticeStarted() {
  yield takeEvery(types.NOTICE_BY_DATE_REMOVE_STARTED, removeNotice);
}
